import React from 'react';
import { formatCurrency } from 'utils';
import { Box, Stack } from '@mui/material';
import { FormInput } from 'components/form';
import { chargeCalc } from 'modules/equipment';
import { BasePrice } from 'modules/equipment/types';
import { useFormContext, useWatch } from 'react-hook-form';
import { ListingForm } from 'components/shared/TableListing/types';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';

import { equipmentCalc } from '../../../../../lib/equipmentCalc';
import { CHARGE_ROW_HEIGHT } from '../../../../../constants/form';
import { QuoteChargeListingEntity, QuoteChargeType, QuoteEquipmentAttachmentWithCharge } from '../../../../../types';

interface Props {
  rowIndex: number;
  type: keyof Pick<BasePrice, 'netPrice' | 'retailPrice'>;
}

export const ReadOnlyValueCell = ({ type, rowIndex }: Props) => {
  const { control } = useFormContext<ListingForm<QuoteChargeListingEntity>>();

  const { type: cellType, equipment, charge } = useWatch({ control, name: `bodyFields.${rowIndex}` });

  const calculators: Record<
    QuoteChargeType,
    Record<keyof Pick<BasePrice, 'retailPrice' | 'netPrice'>, () => number | null>
  > = {
    [QuoteChargeType.Invoice]: {
      retailPrice: () => charge && chargeCalc.calcRetailPrice(charge),
      netPrice: () => charge && chargeCalc.calcNetPrice(charge),
    },
    [QuoteChargeType.Equipment]: {
      retailPrice: () => equipment && equipmentCalc.calcRetailPrice(equipment),
      netPrice: () => equipment && equipmentCalc.calcNetPrice(equipment),
    },
  } as const;

  const attachmentsCalculators: Record<
    keyof Pick<BasePrice, 'retailPrice' | 'netPrice'>,
    (attachment: QuoteEquipmentAttachmentWithCharge) => number | null
  > = {
    retailPrice: attachment => attachment && equipmentCalc.calcAttachmentsRetailPrice(attachment),
    netPrice: attachment => attachment && equipmentCalc.calcAttachmentsNetPrice(attachment),
  } as const;

  const value = calculators[cellType]?.[type]?.();

  if (value == null) return null;

  const renderWithAttachments = cellType === QuoteChargeType.Equipment && !!equipment?.attachments?.length;

  return (
    <Stack width="100%" height="100%" gap="0.5rem">
      <Box sx={{ paddingBottom: renderWithAttachments ? '2.5rem' : 0 }}>
        <FormInput variant="outlined" disabled value={formatCurrency(value, true)} />
      </Box>

      <RenderWithCondition condition={renderWithAttachments}>
        {equipment?.attachments?.map((attachment, index) => (
          <Box height={CHARGE_ROW_HEIGHT}>
            <FormInput
              disabled
              key={index}
              variant="outlined"
              value={formatCurrency(attachmentsCalculators[type](attachment), true)}
            />
          </Box>
        ))}
      </RenderWithCondition>
    </Stack>
  );
};
